import { CommonModule } from '@angular/common';
import { Component, computed, OnDestroy, OnInit, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';

import { DatatableController } from '../shared/datatable.controller';
import { formatCompactOrIsoDate } from '../shared/trade-formatters';
import { DashboardService } from './dashboard.service';
import {
  EMPTY_PENDING_ITEMS,
  PendingAnyRow,
  PendingBackOfficeRow,
  PendingBondDefinitionRow,
  PendingCategoryKey,
  PendingCategoryView,
  PendingDiscountNoteRow,
  PendingInitReopenBondRow,
  PendingItemsModel,
  PendingLinkTarget,
  PendingNewItemRow,
  PendingNoVerifiedBondRow,
  PendingRepurcBondRow,
  PendingSwapRow,
  PENDING_CATEGORIES,
} from './dashboard.types';

const ALL_USERS_KEY = '__all_users__';
const UNASSIGNED_USER_LABEL = 'Unassigned';

type PendingUserCardView = {
  key: string;
  label: string;
  count: number;
  isAllUsers: boolean;
};

@Component({
  selector: 'app-dashboard',
  imports: [CommonModule, RouterLink],
  templateUrl: './dashboard.html',
  styleUrl: './dashboard.css',
})
export class Dashboard implements OnInit, OnDestroy {
  private readonly datatableControllers = new Map<PendingCategoryKey, DatatableController>(
    PENDING_CATEGORIES.map((category) => [
      category.key,
      new DatatableController(category.datatableId),
    ]),
  );
  private requestSubscription?: Subscription;

  protected readonly isLoading = signal(false);
  protected readonly errorMessage = signal('');
  protected readonly model = signal<PendingItemsModel>(EMPTY_PENDING_ITEMS);
  protected readonly selectedUserKey = signal(ALL_USERS_KEY);
  protected readonly activeCategory = signal<PendingCategoryKey>(PENDING_CATEGORIES[0].key);

  protected readonly userCards = computed<PendingUserCardView[]>(() => {
    const currentModel = this.model();
    const allRows = this.getAllPendingRows(currentModel);
    const userMap = new Map<string, PendingUserCardView>();

    for (const row of allRows) {
      const userLabel = this.normalizeDoneUser(row.doneUser);
      const key = userLabel.toLowerCase();
      const existing = userMap.get(key);

      if (existing) {
        existing.count += 1;
      } else {
        userMap.set(key, {
          key,
          label: userLabel,
          count: 1,
          isAllUsers: false,
        });
      }
    }

    const sortedUsers = Array.from(userMap.values()).sort((left, right) =>
      right.count - left.count || left.label.localeCompare(right.label),
    );

    return [
      {
        key: ALL_USERS_KEY,
        label: 'All Users',
        count: allRows.length,
        isAllUsers: true,
      },
      ...sortedUsers,
    ];
  });

  protected readonly filteredModel = computed<PendingItemsModel>(() => {
    const selectedUserKey = this.selectedUserKey();
    const currentModel = this.model();

    if (selectedUserKey === ALL_USERS_KEY) {
      return currentModel;
    }

    return {
      alertDate: currentModel.alertDate,
      newItems: this.filterBySelectedUser(currentModel.newItems, selectedUserKey),
      pendingInitReopenBonds: this.filterBySelectedUser(currentModel.pendingInitReopenBonds, selectedUserKey),
      pendingDiscountNotes: this.filterBySelectedUser(currentModel.pendingDiscountNotes, selectedUserKey),
      pendingSwaps: this.filterBySelectedUser(currentModel.pendingSwaps, selectedUserKey),
      pendingRepurcBonds: this.filterBySelectedUser(currentModel.pendingRepurcBonds, selectedUserKey),
      pendingBondDefinitions: this.filterBySelectedUser(currentModel.pendingBondDefinitions, selectedUserKey),
      pendingBackOffices: this.filterBySelectedUser(currentModel.pendingBackOffices, selectedUserKey),
      noVerifiedBonds: this.filterBySelectedUser(currentModel.noVerifiedBonds, selectedUserKey),
    };
  });

  protected readonly selectedUserLabel = computed(() =>
    this.userCards().find((card) => card.key === this.selectedUserKey())?.label ?? 'All Users',
  );

  protected readonly categories = computed<PendingCategoryView[]>(() => {
    const model = this.filteredModel();

    return PENDING_CATEGORIES.map((category) => ({
      ...category,
      count: model[category.key].length,
    }));
  });

  protected readonly totalPendingCount = computed(() =>
    this.categories().reduce((total, category) => total + category.count, 0),
  );

  protected readonly hasAnyPendingItem = computed(() => this.totalPendingCount() > 0);

  protected readonly alertDateLabel = computed(() => {
    const alertDate = this.model().alertDate;
    return alertDate ? formatCompactOrIsoDate(alertDate) : '';
  });

  constructor(private readonly dashboardService: DashboardService) {}

  ngOnInit(): void {
    this.loadPendingItems();
  }

  ngOnDestroy(): void {
    this.requestSubscription?.unsubscribe();
  }

  protected onRetry(): void {
    this.loadPendingItems();
  }

  protected onSelectCategory(category: PendingCategoryView): void {
    if (category.count === 0) {
      return;
    }

    this.activeCategory.set(category.key);
    this.queueActiveDatatableInitialization();
  }

  protected onSelectUserCard(userCard: PendingUserCardView): void {
    if (this.selectedUserKey() === userCard.key) {
      return;
    }

    this.selectedUserKey.set(userCard.key);
    this.selectFirstCategoryWithItems();
    this.queueActiveDatatableInitialization();
  }

  protected isActiveCategory(key: PendingCategoryKey): boolean {
    return this.activeCategory() === key;
  }

  protected isActiveUserCard(key: string): boolean {
    return this.selectedUserKey() === key;
  }

  protected trackByCategory(index: number, category: PendingCategoryView): string {
    return category.key;
  }

  protected trackByUserCard(index: number, userCard: PendingUserCardView): string {
    return userCard.key;
  }

  protected trackByRow(index: number, row: PendingAnyRow): string {
    return `${row.tradeId}-${row.dealId}-${index}`;
  }

  protected formatDate(value: string): string {
    return value ? formatCompactOrIsoDate(value) : '—';
  }

  protected display(value: string): string {
    return value ? value : '—';
  }

  protected newItems(): PendingNewItemRow[] {
    return this.filteredModel().newItems;
  }

  protected initReopenBonds(): PendingInitReopenBondRow[] {
    return this.filteredModel().pendingInitReopenBonds;
  }

  protected discountNotes(): PendingDiscountNoteRow[] {
    return this.filteredModel().pendingDiscountNotes;
  }

  protected swaps(): PendingSwapRow[] {
    return this.filteredModel().pendingSwaps;
  }

  protected repurchases(): PendingRepurcBondRow[] {
    return this.filteredModel().pendingRepurcBonds;
  }

  protected bondDefinitions(): PendingBondDefinitionRow[] {
    return this.filteredModel().pendingBondDefinitions;
  }

  protected backOffices(): PendingBackOfficeRow[] {
    return this.filteredModel().pendingBackOffices;
  }

  protected canceledBonds(): PendingNoVerifiedBondRow[] {
    return this.filteredModel().noVerifiedBonds;
  }

  protected datatableRenderKey(): string {
    const activeCategory = this.activeCategory();
    const activeRows = this.filteredModel()[activeCategory].length;
    return `${this.selectedUserKey()}::${activeCategory}::${activeRows}`;
  }

  protected backOfficeLink(row: PendingBackOfficeRow): PendingLinkTarget {
    const type = row.type.trim().toUpperCase();

    if (type === 'BOND' || type === 'REPURC') {
      return row.tradeId ? { kind: 'bond', id: row.tradeId } : { kind: 'none' };
    }

    if (type === 'SWAP') {
      return row.tradeId ? { kind: 'swap', id: row.tradeId } : { kind: 'none' };
    }

    if (type === 'BOND_DEF') {
      return row.securityId ? { kind: 'bondDefinition', id: row.securityId } : { kind: 'none' };
    }

    return { kind: 'none' };
  }

  protected backOfficeIdLabel(row: PendingBackOfficeRow): string {
    const type = row.type.trim().toUpperCase();
    const value = type === 'BOND_DEF' ? row.securityId : row.tradeId;

    return value || '—';
  }

  protected backOfficeDealId(row: PendingBackOfficeRow): string {
    return this.isBackOfficeType(row, ['BOND', 'REPURC']) ? this.display(row.dealId) : '';
  }

  protected backOfficeType(row: PendingBackOfficeRow): string {
    return this.isBackOfficeType(row, ['REPURC']) ? 'BOND' : this.display(row.type);
  }

  protected backOfficeSeriesNumber(row: PendingBackOfficeRow): string {
    return this.isBackOfficeType(row, ['BOND_DEF']) ? this.display(row.bondPricingSupplement) : '';
  }

  protected backOfficeTranche(row: PendingBackOfficeRow): string {
    return this.isBackOfficeType(row, ['BOND']) ? this.display(row.tranche) : '';
  }

  protected backOfficeLegalDocument(row: PendingBackOfficeRow): string {
    return this.isBackOfficeType(row, ['BOND']) ? this.display(row.hasLegalDocument) : '';
  }

  protected backOfficeTermSheet(row: PendingBackOfficeRow): string {
    return this.isBackOfficeType(row, ['BOND', 'SWAP', 'REPURC'])
      ? this.display(row.hasTermSheet)
      : '';
  }

  protected backOfficeRta(row: PendingBackOfficeRow): string {
    return this.isBackOfficeType(row, ['BOND']) ? this.display(row.hasRta) : '';
  }

  private isBackOfficeType(row: PendingBackOfficeRow, types: string[]): boolean {
    return types.includes(row.type.trim().toUpperCase());
  }

  /** Resolves the detail route for rows that carry an explicit trade/security type. */
  protected resolveTypedLink(row: {
    tradeId: string;
    securityId: string;
    tradeType: string;
    securityType: string;
  }): PendingLinkTarget {
    const tradeType = row.tradeType.trim().toUpperCase();
    const securityType = row.securityType.trim().toUpperCase();

    if (tradeType === 'BOND_DEF' || securityType === 'BOND_DEF') {
      return row.securityId ? { kind: 'bondDefinition', id: row.securityId } : { kind: 'none' };
    }

    if (
      tradeType === 'SWAP'
      || tradeType === 'SWOPT'
      || securityType === 'SWAP'
      || securityType === 'SWOPT'
    ) {
      return row.tradeId ? { kind: 'swap', id: row.tradeId } : { kind: 'none' };
    }

    if (row.tradeId) {
      return { kind: 'bond', id: row.tradeId };
    }

    return row.securityId ? { kind: 'bondDefinition', id: row.securityId } : { kind: 'none' };
  }

  protected typedLinkLabel(row: { tradeId: string; securityId: string }): string {
    return row.tradeId || row.securityId || '—';
  }

  protected linkCommands(target: PendingLinkTarget): unknown[] {
    switch (target.kind) {
      case 'bond':
        return ['/bond-detail', target.id];
      case 'swap':
        return ['/swap-detail', target.id];
      case 'bondDefinition':
        return ['/bond-definition', target.id];
      default:
        return [];
    }
  }

  protected bondLink(tradeId: string): unknown[] {
    return ['/bond-detail', tradeId];
  }

  protected swapLink(tradeId: string): unknown[] {
    return ['/swap-detail', tradeId];
  }

  protected bondDefinitionLink(securityId: string): unknown[] {
    return ['/bond-definition', securityId];
  }

  private loadPendingItems(): void {
    this.isLoading.set(true);
    this.errorMessage.set('');
    this.requestSubscription?.unsubscribe();

    this.requestSubscription = this.dashboardService.getPendingItems().subscribe({
      next: (model) => {
        this.model.set(model);
        this.isLoading.set(false);
        this.ensureSelectedUserCardExists();
        this.ensureActiveCategoryHasItems();
        this.queueActiveDatatableInitialization();
      },
      error: () => {
        this.model.set(EMPTY_PENDING_ITEMS);
        this.errorMessage.set('Unable to load pending items. Please try again.');
        this.isLoading.set(false);
      },
    });
  }

  private activeDatatableController(): DatatableController | undefined {
    return this.datatableControllers.get(this.activeCategory());
  }

  /** Keeps the landing tab useful by focusing the first category that actually has rows. */
  private ensureActiveCategoryHasItems(): void {
    const categories = this.categories();
    const current = categories.find((category) => category.key === this.activeCategory());

    if (current && current.count > 0) {
      return;
    }

    this.selectFirstCategoryWithItems();
  }

  private ensureSelectedUserCardExists(): void {
    const selected = this.selectedUserKey();
    if (selected === ALL_USERS_KEY) {
      return;
    }

    const exists = this.userCards().some((card) => card.key === selected);
    if (!exists) {
      this.selectedUserKey.set(ALL_USERS_KEY);
    }
  }

  private filterBySelectedUser<T extends { doneUser: string }>(rows: T[], selectedUserKey: string): T[] {
    return rows.filter((row) => this.normalizeDoneUser(row.doneUser).toLowerCase() === selectedUserKey);
  }

  private getAllPendingRows(model: PendingItemsModel): PendingAnyRow[] {
    return [
      ...model.newItems,
      ...model.pendingInitReopenBonds,
      ...model.pendingDiscountNotes,
      ...model.pendingSwaps,
      ...model.pendingRepurcBonds,
      ...model.pendingBondDefinitions,
      ...model.pendingBackOffices,
      ...model.noVerifiedBonds,
    ];
  }

  private normalizeDoneUser(value: string): string {
    const normalized = value.trim();
    return normalized ? normalized : UNASSIGNED_USER_LABEL;
  }

  private queueActiveDatatableInitialization(): void {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.activeDatatableController()?.initialize();
      });
    });
  }

  private selectFirstCategoryWithItems(): void {
    const categories = this.categories();
    const firstWithItems = categories.find((category) => category.count > 0);
    this.activeCategory.set(firstWithItems ? firstWithItems.key : PENDING_CATEGORIES[0].key);
  }
}
